import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import CustomTextInput from '../../../components/CustomTextInput';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { get, map } from 'lodash';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSignupHandler } from '../dataHandlers/signupHandler';
import { yupResolver } from '@hookform/resolvers/yup';
import appConfig from '../../../config/appConfig';
import OptionalExternalLink from '../../../components/OptionalExternalLink';

const SignupValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  name: Yup.string()
    .required('Name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(25, 'Name must be at most 25 characters'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[@$!%*?&]/, 'Password must contain at least one special character (@, $, !, %, *, ?, &)')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
  termsAndCondition: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
});

const SignupPasswordForm = ({ email }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(SignupValidationSchema),
    defaultValues: {
      email: email || '',
      termsAndCondition: false,
    },
  });

  const onSuccess = (data) => {
    setLoading(false);
    // Check if user needs verification
    if (data?.verified === false) {
      navigate('/signup', { replace: true, state: { email: email, stage: 'verification' } });
    } else {
      navigate('/login');
    }
  };

  const { onSubmit, error } = useSignupHandler({ onSuccess });

  useEffect(() => {
    map(error, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, error]);

  const handleSave = async (data) => {
    setLoading(true);
    await onSubmit(data);
    setLoading(false);
  };

  const formData = watch();

  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
      </Stack>

      <Stack spacing={2}>
        <CustomTextInput name="email" label="Email" control={control} errors={errors} />
        <CustomTextInput name="name" label="Name" control={control} errors={errors} />
        <CustomTextInput
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <CustomTextInput
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          label="Confirm Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Grid container spacing={0}>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: formData.password?.length >= 8 ? 'green' : '#ff0000' }}>
              Minimum 8 characters.
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[A-Z]/.test(formData.password) ? 'green' : '#ff0000' }}>
              At least 1 uppercase letter.
            </Typography>
          </Grid>

          <Grid size={6}>
            <Typography variant="caption" sx={{ color: /[0-9]/.test(formData.password) ? 'green' : '#ff0000' }}>
              At least 1 number.
            </Typography>
          </Grid>
          <Grid size={6}>
            <Typography
              variant="caption"
              sx={{ color: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'green' : '#ff0000' }}
            >
              At least one special character.
            </Typography>
          </Grid>
        </Grid>
        <Controller
          name="termsAndCondition"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  color="primary"
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                  }}
                />
              }
              label={
                <Box>
                  I agree to the{' '}
                  <OptionalExternalLink href={appConfig.termsUrl}>terms & conditions</OptionalExternalLink> and{' '}
                  <OptionalExternalLink href={appConfig.privacyUrl}>privacy policy</OptionalExternalLink> of{' '}
                  {appConfig.brandName} platform.
                </Box>
              }
            />
          )}
        />
        {errors.termsAndCondition && (
          <FormHelperText error>{get(errors, 'termsAndCondition.message', '')}</FormHelperText>
        )}
        <Button type="button" variant="contained" className="submit" onClick={handleSubmit(handleSave)}>
          {loading ? 'Signing up...' : 'Sign Up'}
        </Button>
        <Typography variant="body2">
          <Link to="/login" style={{ textDecoration: 'none' }}>
            Already have an account? Sign In
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default SignupPasswordForm;
